import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { History } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, fmtChanges } from "@/lib/util";
import { Loader, LocLink } from "@/components/common";
import { PageTitle, SearchBox, Table, Td, useDebounced } from "@/pages/admin/ui";
import { DateRange, Pager } from "@/pages/admin/Trips";

const ROLE = { admin: "مدير", staff: "موظف", driver: "سائق", system: "النظام" };
const ENT = { salary: "راتب", archive: "أرشيف", trip: "نقلة", invoice: "فاتورة", driver: "سائق", vehicle: "شاحنة", violation: "مخالفة", notification: "إشعار", document: "مستند", fee: "رسوم", user: "مستخدم", settings: "إعدادات", backup: "نسخ احتياطي", export: "تصدير" };

export default function Audit() {
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const [f, setF] = useState({ entity: "", date_from: "", date_to: "", role: sp.get("role") || "", user_id: sp.get("user_id") || "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [staff, setStaff] = useState([]);
  useEffect(() => { if (user.role === "admin") { api.get("/staff").then((r) => setStaff(r.data.items.filter((u) => u.role === "staff"))).catch(() => {}); } }, [user.role]);
  useEffect(() => { api.get("/audit", { params: { q: dq, ...f, page } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, [dq, f, page]);
  const set = (k) => (v) => { setPage(1); setF({ ...f, [k]: v }); };
  return (
    <div>
      <PageTitle title="سجل النشاط" sub="لا تُحذف السجلات القديمة" icon={History} />
      <div className="flex flex-wrap gap-3 mb-4">
        <SearchBox value={q} onChange={(v) => { setPage(1); setQ(v); }} placeholder="اسم المستخدم أو الإجراء" testid="audit-search" />
        <select data-testid="audit-entity-filter" className="field field-sm w-40" value={f.entity} onChange={(e) => set("entity")(e.target.value)}>
          <option value="">كل السجلات</option>{Object.entries(ENT).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <select data-testid="audit-role-filter" className="field field-sm w-40" value={f.role} onChange={(e) => set("role")(e.target.value)}>
          <option value="">كل المستخدمين</option>{Object.entries(ROLE).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        {staff.length > 0 && <select data-testid="audit-staff-filter" className="field field-sm w-44" value={f.user_id} onChange={(e) => { setPage(1); setF({ ...f, user_id: e.target.value, role: e.target.value ? "" : f.role }); }}>
          <option value="">كل الموظفين</option>{staff.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}</select>}
        <DateRange from={f.date_from} to={f.date_to} setFrom={set("date_from")} setTo={set("date_to")} />
      </div>
      {!data ? <Loader /> : (
        <Table head={["الوقت", "المستخدم", "الإجراء", "السجل", "الموقع", "قبل", "بعد"]} testid="audit-table">
          {data.items.map((a) => <tr key={a.id}>
            <Td className="whitespace-nowrap">{fmtDateTime(a.at)}</Td><Td>{a.user_name}<div className={`text-xs ${a.role === "staff" ? "text-orange-600 font-bold" : "text-slate-400"}`}>{ROLE[a.role] || a.role}</div></Td><Td className="font-bold">{a.action}{a.note && <div className="text-xs text-slate-500">{a.note}</div>}</Td>
            <Td>{ENT[a.entity] || a.entity}</Td><Td>{a.location ? <LocLink loc={a.location} /> : "—"}</Td>
            <Td className="text-xs max-w-[260px]">{fmtChanges(a.before)}</Td>
            <Td className="text-xs max-w-[260px]">{fmtChanges(a.after)}</Td>
          </tr>)}
        </Table>
      )}
      {data && <Pager page={page} total={data.total} size={100} setPage={setPage} />}
    </div>
  );
}
